import {createSelector} from 'reselect';
import memoize from 'lodash/memoize';

import {
  ILoadedNoteflowExecutionAction,
  ILoadedNoteflowExecutionLogsAction,
  ILoadedNoteflowExecutionsAction,
  ILoadedReRunsAction,
  IUnsetNoteflowExecutionsAction,
  NoteflowActions,
  NoteflowActionTypes
} from '../actions/noteflowActions';

import {ApiStatus} from '../../models';
import {IState} from './index';

export const initialNoteflowState: INoteflowState = {
  executeRequest: false,
  executingStatus: null,
  executions: null, // or "runs"
  reRuns: null, // so we don't mix it with `executions`
  canLoadMore: false, // used for pagination, if there are no more executions to load
  loadingExecutionsStatus: null,
  loadingReRunsStatus: null,
  executionLogs: null,
  searchResults: {
    reports: null,
    users: null,
  },
  searchLoadingStatus: null,
  filters: null, // search results filters
};

export interface INoteflowState {
  executeRequest: boolean;
  executingStatus: ApiStatus | null;
  executions: null | any;
  reRuns: null | any;
  /*
   * dict of key: source_uid, values: executions
   * - source can report
   */
  canLoadMore: boolean;
  loadingExecutionsStatus: ApiStatus | null;
  loadingReRunsStatus: ApiStatus | null;
  executionLogs: null | any;
  searchResults: {
    reports: null | any;
    users: null | any;
  };
  searchLoadingStatus: ApiStatus | null;
  filters: null | any;
}

export default function noteflowReducer(
  state: INoteflowState = initialNoteflowState,
  action:
    NoteflowActions
): INoteflowState {
  switch (action.type) {
    case NoteflowActionTypes.EXECUTE_NOTEFLOW:
      return {
        ...state,
        executeRequest: true,
        executingStatus: ApiStatus.LOADING,
      }
    case NoteflowActionTypes.EXECUTED_NOTEFLOW:
      return {
        ...state,
        executeRequest: false,
        executingStatus: ApiStatus.LOADED,
      }
    case NoteflowActionTypes.UNSET_EXECUTE_REQUEST_FLAG:
    case NoteflowActionTypes.EXECUTING_NOTEFLOW_FAILED:
      return {
        ...state,
        executeRequest: false,
        executingStatus: null,
      }
    case NoteflowActionTypes.LOAD_NOTEFLOW_EXECUTIONS:
      return {
        ...state,
        loadingExecutionsStatus: ApiStatus.LOADING
      }
    case NoteflowActionTypes.LOADING_NOTEFLOW_EXECUTIONS_FAILED:
      return {
        ...state,
        loadingExecutionsStatus: ApiStatus.FAILED
      }
    case NoteflowActionTypes.LOADED_NOTEFLOW_EXECUTION:
      return handleLoadedNoteflowExecution(state, action);
    case NoteflowActionTypes.LOADED_NOTEFLOW_EXECUTIONS:
      return handleLoadedNoteflowExecutions(state, action);
    case NoteflowActionTypes.LOAD_RE_RUNS:
      return {
        ...state,
        loadingReRunsStatus: ApiStatus.LOADING,
      }
    case NoteflowActionTypes.LOADING_RE_RUNS_FAILED:
      return {
        ...state,
        loadingReRunsStatus: ApiStatus.FAILED,
      }
    case NoteflowActionTypes.LOADED_RE_RUNS:
      return handleLoadedReRuns(state, action);
    case NoteflowActionTypes.SET_NOTEFLOW_FILTERS:
      return {
        ...state,
        filters: action.payload,
      }
    case NoteflowActionTypes.UNSET_NOTEFLOW_FILTERS:
      return {
        ...state,
        filters: null,
        searchResults: {
          reports: null,
          users: null,
        },
        searchLoadingStatus: null,
      }
    case NoteflowActionTypes.UNSET_NOTEFLOW_EXECUTIONS:
      return handleUnsetNoteflowExecutions(state, action);
    case NoteflowActionTypes.LOADED_NOTEFLOW_EXECUTION_LOGS:
      return handleLoadedNoteflowExecutionLogs(state, action);
    case NoteflowActionTypes.LOAD_SEARCH_REPORT:
    case NoteflowActionTypes.LOAD_SEARCH_USER:
      return {
        ...state,
        searchLoadingStatus: ApiStatus.LOADING,
      }
    case NoteflowActionTypes.LOADED_SEARCH_REPORT:
      return {
        ...state,
        searchResults: {
          ...state.searchResults,
          reports: action.payload,
        },
        searchLoadingStatus: ApiStatus.LOADED,
      }
    case NoteflowActionTypes.LOADED_SEARCH_USER:
      return {
        ...state,
        searchResults: {
          ...state.searchResults,
          users: action.payload,
        },
        searchLoadingStatus: ApiStatus.LOADED,
      }
    case NoteflowActionTypes.RESET_SEARCH:
      // TODO - implement action.payload to know which search to reset
      return {
        ...state,
        searchResults: {
          reports: null,
          users: null,
        },
        searchLoadingStatus: null,
      }
    default:
      return state;
  }
}

// Functions

function handleLoadedNoteflowExecutions(state: INoteflowState, action: ILoadedNoteflowExecutionsAction) {
  const reload = action.payload.reload || false;
  const newExecutions = action.payload.result || [];
  const page_size = action.payload.page_size || 0;

  return {
    ...state,
    executions: reload ? [...newExecutions] : [
      ...state.executions || [],
      ...newExecutions,
    ],
    canLoadMore: reload ? false : (action.payload?.result?.length === page_size),
    loadingExecutionsStatus: ApiStatus.LOADED
  }
}

function handleLoadedReRuns(state: INoteflowState, action: ILoadedReRunsAction): INoteflowState {
  const newExecutions = action.payload.result || [];

  return {
    ...state,
    reRuns: [
      ...state.reRuns || [],
      ...newExecutions,
    ],
    loadingReRunsStatus: ApiStatus.LOADED
  }
}

function handleLoadedNoteflowExecution(state: INoteflowState, action: ILoadedNoteflowExecutionAction) {
  let newExecutions = state.executions || [];

  if (newExecutions.length === 0) {
    newExecutions.push(action.payload.result);
  } else {
    let found = false;

    newExecutions = newExecutions.map((execution: any) => {
      if (execution.flow_run_id === action.payload.result.flow_run_id) {
        found = true;
        return action.payload.result;
      }
      return execution;
    });

    if (!found) {
      newExecutions = [
        action.payload.result, // add first
        ...newExecutions,
      ]
    }
  }

  return {
    ...state,
    executions: newExecutions,
    loadingExecutionsStatus: ApiStatus.LOADED,
  }
}

function handleUnsetNoteflowExecutions(state: INoteflowState, action: IUnsetNoteflowExecutionsAction) {
  return {
    ...state,
    executions: null,
    reRuns: null,
    canLoadMore: false,
    loadingExecutionsStatus: null,
    loadingReRunsStatus: null,
    executionLogs: null,
    searchResults: {
      reports: null,
      users: null,
    },
    searchLoadingStatus: null,
  }
}

function handleLoadedNoteflowExecutionLogs(state: INoteflowState, action: ILoadedNoteflowExecutionLogsAction) {
  return {
    ...state,
    executionLogs: action.payload, // TODO: append to existing logs, not replace
  }
}

// Selectors

export const getExecutionLogs = memoize(
  (flowRunId: string | null | undefined) => createSelector(
    (state: IState) => state.noteflow.executionLogs,
    (executionLogs: any | null): any => {
      return executionLogs?.filter((log: any) => log.flow_run_id === flowRunId) || [];
    },
));

const hashFn = (flowRunId: string, taskRunId: string) => `${flowRunId}_${taskRunId}`;

export const getExecutionTaskLogs = memoize(
  (flowRunId: string, taskRunId: string) => createSelector(
    (state: IState) => state.noteflow.executionLogs,
    (executionLogs: any | null): any => {
      return executionLogs
        ?.filter((log: any) => log.flow_run_id === flowRunId && log.task_run_id === taskRunId) || [];
    },
), hashFn);

export const getNoteflowSelector = memoize((flowRunId: string | null | undefined) => createSelector(
  (state: IState) => state.noteflow.executions,
  (executions: any | null): any => {
    return executions
      ?.find((execution: any) => flowRunId === (execution.flow_run_id || execution.uid)) || null;
  }
));
